import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { portfolioService } from '../services/portfolioService';
import { useCapabilities } from '../state/CapabilitiesContext';
import { createResourceCache } from './resourceCache';
import { useResource } from './usePortfolio';
import BlockState from './Components/BlockState';
import Disclosure, { DISCLOSURE_DURATION } from './Components/Disclosure';
import Gallery, { safeUrl } from './Components/Gallery';
import InlineAnswers from './Components/InlineAnswers';
import { usePublicChatContext } from './usePublicChatContext';
import { inlineScopeKey } from './useInlineAnswers';
import { STATIC_PROFILE } from './staticProfile';
import './portfolio.css';

// "All work" is a browser-side view over every published material in their published
// order; categories hold each material's primary area. Retired category slugs that
// covered everything resolve to this view so existing links keep working.
export const ALL_WORK = Object.freeze({ id: 'all', slug: 'all', title: 'All work', materialIds: null });
const ALL_WORK_ALIASES = ['all', 'engineering-approach'];
const shortTitle = (title) => title.split(':')[0].trim();

function categoryMaterials(data, category) {
  if (!data || !category) return [];
  if (category === ALL_WORK) return data.materials;
  const members = data.materials.filter((item) => item.categoryIds.includes(category.id));
  const order = category.materialIds || [];
  const rank = (item) => { const index = order.indexOf(item.id); return index < 0 ? order.length : index; };
  return [...members].sort((a, b) => rank(a) - rank(b));
}

function ProfileIntro({ profile }) {
  const portrait = profile?.portrait;
  return <section id="introduction" className="portfolio-introduction">
    <div><p className="portfolio-eyebrow">{STATIC_PROFILE.eyebrow}</p>
      <h1 id="portfolio-profile-title">{profile?.displayName || 'Portfolio'}</h1>
      {profile?.headline && <p className="portfolio-headline">{profile.headline}</p>}
    </div>
    {safeUrl(portrait?.url) && <figure className="portfolio-portrait">
      <img src={portrait.url} alt={portrait.alt} />
      {portrait.generated && <figcaption>AI portrait</figcaption>}
    </figure>}
  </section>;
}

function Section({ cache, material, section, locale, revision, onContext, active }) {
  const load = useCallback((signal) => portfolioService.section(material.id, section.id, locale, signal),
    [material.id, section.id, locale]);
  const resource = useResource(cache, `${material.id}:${section.id}:${locale}:${revision}`,
    load, active);
  const data = resource.data;
  return <div aria-busy={resource.loading} className="portfolio-section-body">
    <BlockState resource={resource} name="section">
      {data?.contentRevision !== revision ? <p role="status">Content changed. Reopen this material.</p> :
        <><p className="portfolio-body-text">{data?.section.body}</p>
          {onContext && <button onClick={() => onContext(material.id, section.id)}>
            Discuss this section in full chat</button>}</>}
    </BlockState>
  </div>;
}

function Material({ cache, summary, locale, revision, sectionId, selectSection, selectRelated,
  materials, capabilities, answerPanel, onContext, focusHeading, onFocusHandled,
  onRevisionChange, active }) {
  const load = useCallback((signal) => portfolioService.material(summary.id, locale, signal),
    [summary.id, locale]);
  const resource = useResource(cache, `${summary.id}:${locale}:${revision}`, load, active);
  const [gallery, setGallery] = useState(false);
  const material = resource.data?.material;
  // Ask for the first gallery page only once the material is open, so the button
  // appears just for materials that actually have published media.
  const loadGallery = useCallback((signal) => portfolioService.gallery(summary.id, locale, null, signal),
    [summary.id, locale]);
  const galleryProbe = useResource(cache, `gallery:${summary.id}:${locale}:${revision}`, loadGallery,
    active && Boolean(material));
  const hasGallery = galleryProbe.data?.contentRevision === revision &&
    galleryProbe.data.items.length > 0;
  const galleryTitle = material ? `${shortTitle(material.title)} gallery` : 'Project gallery';
  // The first section ("My part") opens with the material; closing it afterwards sticks
  // until the material itself is closed again.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!active) { autoOpened.current = false; return; }
    if (!material || autoOpened.current) return;
    autoOpened.current = true;
    if (!sectionId && material.sections.length) selectSection(material.sections[0].id, { replace: true });
  }, [active, material, sectionId, selectSection]);
  // Between the material arriving and its first section opening, keep the material-level
  // answer panel hidden so it does not flash (or take input) for a single frame.
  const autoOpening = active && Boolean(material) && !sectionId && !autoOpened.current &&
    material.sections.length > 0;
  const materialAnswers = !sectionId && !autoOpening && answerPanel;
  useEffect(() => {
    if (active && focusHeading && material) {
      const toggle = document.getElementById(`toggle-${summary.id}`);
      toggle?.focus(); toggle?.scrollIntoView?.({ block: 'nearest' });
      onFocusHandled();
    }
  }, [active, focusHeading, material, summary.id, onFocusHandled]);
  useEffect(() => { if (!active) setGallery(false); }, [active]);
  return <div className="portfolio-detail" aria-busy={resource.loading}>
    <BlockState resource={resource} name="material">
      {resource.data?.contentRevision !== revision ?
        <p role="status">Content changed. Refresh the page to review the current version.</p> : material &&
        <div className={`portfolio-material-layout${materialAnswers ? ' has-material-answers' : ''}`}>
          <div className="portfolio-material-content">
          {material.sections.map((section) => <section key={section.id} id={`section-${section.id}`}>
            <h3><button id={`toggle-section-${section.id}`} aria-expanded={sectionId === section.id}
              aria-controls={`body-${section.id}`} onClick={() => selectSection(sectionId === section.id ? null : section.id)}>
              {section.title}</button></h3>
            <Disclosure open={active && sectionId === section.id} id={`body-${section.id}`}
              labelledBy={`toggle-section-${section.id}`}>
              {(sectionActive) => <>
                <Section cache={cache} material={material} section={section} locale={locale}
                  revision={revision} active={sectionActive}
                  onContext={sectionActive && capabilities.extendedChat ? onContext : null} />
                {sectionActive && answerPanel?.(material, section)}
              </>}
            </Disclosure>
          </section>)}
          {sectionId && !material.sections.some((s) => s.id === sectionId) &&
            <p role="status">This section is unavailable.</p>}
          <div className="portfolio-actions">
            {hasGallery && <button onClick={() => setGallery(true)}>Open {galleryTitle}</button>}
            {active && capabilities.extendedChat && <button onClick={() => onContext(material.id)}>
              Discuss in full chat</button>}
            {material.relatedMaterialIds.map((id) => {
              const related = materials.find((item) => item.id === id);
              return related && <button key={id} onClick={() => selectRelated(related)}>
                Related: {related.title}</button>;
            })}
          </div>
          </div>
          {active && materialAnswers && answerPanel(material, null)}
        </div>}
    </BlockState>
    {active && gallery && <Gallery title={galleryTitle} materialId={summary.id} locale={locale} revision={revision} onRevisionChange={onRevisionChange} onClose={() => setGallery(false)} />}
  </div>;
}

export default function PortfolioPage() {
  const location = useLocation(); const navigate = useNavigate();
  const capabilities = useCapabilities();
  const cache = useMemo(createResourceCache, []);
  const threads = useMemo(() => new Map(), []);
  const [gallery, setGallery] = useState(false);
  const [focusHeading, setFocusHeading] = useState(() => new URLSearchParams(location.search).has('material'));
  const onFocusHandled = useCallback(() => setFocusHeading(false), []);
  const parameters = new URLSearchParams(location.search);
  const locale = parameters.get('locale') || 'en';
  const load = useCallback((signal) => portfolioService.bootstrap(locale, signal), [locale]);
  const bootstrap = useResource(cache, `bootstrap:${locale}`, load);
  const data = bootstrap.data;
  const categorySlug = parameters.get('category'); const materialSlug = parameters.get('material');
  let sectionId = null;
  if (location.hash.startsWith('#section-')) {
    try { sectionId = decodeURIComponent(location.hash.slice(9)); }
    catch (_) { sectionId = 'invalid-section'; }
  }
  const category = !data ? null : (data.categories.find((item) => item.slug === categorySlug) ||
    (!categorySlug || ALL_WORK_ALIASES.includes(categorySlug) ? ALL_WORK : null));
  const visibleMaterials = categoryMaterials(data, category);
  const selected = visibleMaterials.find((item) => item.slug === materialSlug);
  const setSelection = (nextCategory, material, section = null, focus = false, replace = false) => {
    const query = new URLSearchParams();
    if (locale !== 'en') query.set('locale', locale);
    if (nextCategory && nextCategory !== ALL_WORK) query.set('category', nextCategory.slug);
    if (material) query.set('material', material.slug);
    setFocusHeading(focus);
    navigate(`/?${query}${section ? `#section-${encodeURIComponent(section)}` : ''}`, { replace });
  };
  const categoryLink = (item) => {
    const query = new URLSearchParams(locale !== 'en' ? { locale } : {});
    if (item !== ALL_WORK) query.set('category', item.slug);
    return `/?${query}`;
  };
  const onDraft = useCallback((draft) => navigate('/chat', { state: { publicDraft: draft } }), [navigate]);
  const discussion = usePublicChatContext({ locale, revision: data?.contentRevision,
    selection: `${location.search}${location.hash}`, enabled: capabilities.extendedChat,
    refresh: bootstrap.retry, onDraft });
  const emphasizedMaterialId = selected?.id || visibleMaterials[0]?.id;
  // Until published content arrives (or if it never does) show the same minimal identity
  // that public/index.html serves without JavaScript.
  const profile = data ? data.profile : STATIC_PROFILE;
  const contactLinks = profile?.contactLinks.filter((link) => safeUrl(link.url, true)) || [];
  return <div className="portfolio-page"
    style={{ '--portfolio-motion-duration': `${DISCLOSURE_DURATION}ms` }}>
    <a className="portfolio-skip" href="#portfolio-main">Skip to content</a>
    <header className="portfolio-header"><Link to="/">thedmytro.me</Link>
      <nav aria-label="Site">
        {contactLinks[0] && <a href={contactLinks[0].url}>{contactLinks[0].label}</a>}
        {capabilities.extendedChat && <Link to="/chat">Full chat</Link>}</nav></header>
    <main id="portfolio-main" tabIndex={-1}>
      {discussion.loading && <p role="status">Checking published context…</p>}
      {discussion.error && <p role="status">Published content could not be confirmed. Review the refreshed material before discussing it.</p>}
      <div className="portfolio-layout">
        <aside className="portfolio-rail" aria-labelledby="portfolio-profile-title">
        <ProfileIntro profile={profile} />
        {profile?.summary && <p className="portfolio-summary">{profile.summary}</p>}
        {data && <nav aria-label="Portfolio categories" className="portfolio-categories">
          {[ALL_WORK, ...data.categories].map((item) => <Link key={item.id} to={categoryLink(item)}
            aria-current={category === item ? 'page' : undefined}>{item.title}</Link>)}
        </nav>}
        {(contactLinks.length > 1 || safeUrl(profile?.instagramUrl)) && <div className="portfolio-profile-links">
          {contactLinks.slice(1).map((link) => <a key={link.url} href={link.url}>{link.label}</a>)}
          {safeUrl(profile?.instagramUrl) && <a href={profile.instagramUrl}>Instagram</a>}
        </div>}
        </aside>
        <div className="portfolio-work">
        <BlockState resource={bootstrap} name="project list">
        {data && <>
          <div className="portfolio-work-heading">
            <h2>{category?.title || 'Portfolio'}</h2>
            {category?.summary && <p>{category.summary}</p>}
          </div>
        {((categorySlug && !category) || (materialSlug && !selected)) && <div role="status">
          <p>This published material or category is unavailable.</p><Link to="/">Return to categories</Link>
        </div>}
        <div className="portfolio-index">
          {visibleMaterials.map((item) =>
            <article key={item.id} className={[
              'portfolio-project', emphasizedMaterialId === item.id && 'featured',
              selected?.id === item.id && 'selected',
            ].filter(Boolean).join(' ')}><div className={[
              'portfolio-card', selected?.id === item.id && 'selected',
              emphasizedMaterialId === item.id && 'featured',
            ].filter(Boolean).join(' ')}>
              <div className="portfolio-card-copy">
              <h3><button id={`toggle-${item.id}`} aria-expanded={selected?.id === item.id}
                aria-controls={`material-${item.id}`} onClick={() => {
                  if (selected?.id === item.id) document.getElementById(`toggle-${item.id}`)?.focus();
                  setSelection(category, selected?.id === item.id ? null : item);
                }}>{item.title}<span className="portfolio-card-arrow" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
                    strokeLinejoin="round" focusable="false">
                    <path d="m9 6 6 6-6 6" />
                  </svg>
                </span></button></h3>
              <p>{item.summary}</p><ul className="portfolio-tags">{item.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul>
              {item.dateLabel && <p className="portfolio-date">{item.dateLabel}</p>}
              </div>
            </div>
              <Disclosure open={selected?.id === item.id} id={`material-${item.id}`}
                labelledBy={`toggle-${item.id}`} className="portfolio-project-details">
                {(active) => <Material key={`${item.id}:${locale}:${data.contentRevision}`}
                cache={cache} summary={item} locale={data.locale} revision={data.contentRevision}
                sectionId={sectionId} focusHeading={focusHeading} onFocusHandled={onFocusHandled}
                selectSection={(id, options) => setSelection(category, item, id, false, options?.replace)}
                selectRelated={(related) => setSelection(category === ALL_WORK ||
                  related.categoryIds.includes(category.id) ? category :
                  data.categories.find((c) => related.categoryIds.includes(c.id)) || ALL_WORK, related, null, true)}
                materials={data.materials} onRevisionChange={bootstrap.retry} capabilities={capabilities} answerPanel={capabilities.inlineAnswers ? (material, section) => {
                  const scope = { materialId: material.id, sectionId: section?.id,
                    label: section?.title || material.title, locale: data.locale, revision: data.contentRevision };
                  return <InlineAnswers key={inlineScopeKey(scope)} store={threads} scope={scope}
                    onDisabled={capabilities.disable} />;
                } : undefined}
                onContext={discussion.prepare} active={active} />}
              </Disclosure>
            </article>)}
        </div>
        {category && !visibleMaterials.length && <p>No published materials yet.</p>}
        <button id="portfolio-gallery-open" className="portfolio-gallery-link" onClick={() => setGallery(true)}>Open creative gallery</button>
        </>}
        </BlockState>
        </div>
      </div>
    </main>
    <footer>thedmytro.me</footer>
    {gallery && <Gallery locale={data?.locale || locale} revision={bootstrap.loading ? null : data?.contentRevision}
      revisionError={Boolean(bootstrap.error)} onRevisionChange={bootstrap.retry} onClose={() => setGallery(false)} />}
  </div>;
}
